import { useState, useEffect } from 'react';
import { apiClient } from '../../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT } from '../table-management/designTokens';

interface GuestEntry {
  firstName: string;
  lastName: string;
  chapter: string;
  meal: string;
}

interface ExistingTicket {
  ticketNumber: number;
  fullName: string;
  chapter: string;
  meal: string;
  seatTable: string;
}

interface MealOption {
  id: string;
  name: string;
  active: boolean;
}

interface FillGapModalProps {
  referenceId: string;
  purchaserName: string;
  tierName: string;
  amount: string | null;
  gap: number;
  existingTickets: number;
  onClose: () => void;
  onComplete: () => void;
}

export function FillGapModal({
  referenceId,
  purchaserName,
  tierName,
  amount,
  gap,
  existingTickets,
  onClose,
  onComplete,
}: FillGapModalProps) {
  const [guests, setGuests] = useState<GuestEntry[]>(
    Array.from({ length: gap }, () => ({ firstName: '', lastName: '', chapter: '', meal: '' })),
  );
  const [autoProvision, setAutoProvision] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [existingRows, setExistingRows] = useState<ExistingTicket[]>([]);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [mealOptions, setMealOptions] = useState<MealOption[]>([]);
  const [completed, setCompleted] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);

  useEffect(() => {
    Promise.all([
      apiClient<ExistingTicket[]>(`/legacy-ball/orders/by-ref/${encodeURIComponent(referenceId)}/tickets`).catch(() => []),
      apiClient<MealOption[]>('/legacy-ball/meal-options').catch(() => []),
    ]).then(([tickets, meals]) => {
      setExistingRows(tickets);
      setMealOptions(meals.filter((m) => m.active));
      setLoadingExisting(false);
    });
  }, [referenceId]);

  const updateGuest = (index: number, field: keyof GuestEntry, value: string) => {
    setGuests((prev) => prev.map((g, i) => (i === index ? { ...g, [field]: value } : g)));
  };

  const handleSubmit = async () => {
    const valid = guests.every((g) => g.firstName.trim() && g.lastName.trim());
    if (!valid) {
      setError('Please fill in first and last name for all guests.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await apiClient(`/legacy-ball/orders/${referenceId}/fill-gap`, {
        method: 'POST',
        body: {
          guests: guests.map((g) => ({
            firstName: g.firstName.trim(),
            lastName: g.lastName.trim(),
            chapter: g.chapter.trim() || null,
            dietaryPref: null,
            mealSelection: g.meal || null,
          })),
          autoProvision,
        },
      });
      // Re-fetch to show updated order
      const updatedTickets = await apiClient<ExistingTicket[]>(
        `/legacy-ball/orders/by-ref/${encodeURIComponent(referenceId)}/tickets`,
      ).catch(() => []);
      setExistingRows(updatedTickets);
      setCreatedCount(guests.length);
      setCompleted(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to create tickets');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    padding: '7px 10px',
    fontSize: '0.82rem',
    border: '1px solid ' + BORDER_LIGHT,
    borderRadius: '6px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '8px 10px',
    fontWeight: 700,
    color: '#6b7280',
    textTransform: 'uppercase',
    fontSize: '0.68rem',
    letterSpacing: '0.4px',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '860px',
          maxHeight: '85vh',
          overflow: 'auto',
          padding: '1.75rem',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: '0 0 4px', fontSize: '1.1rem', fontWeight: 700, color: TEXT_DARK }}>
          Fill Gap — {referenceId}
        </h2>
        <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: TEXT_MUTED }}>
          {purchaserName} · {tierName} · {amount || 'N/A'}
        </p>

        <div
          style={{
            padding: '10px 14px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            marginBottom: '1.25rem',
            fontSize: '0.82rem',
            color: TEXT_DARK,
            border: '1px solid ' + BORDER_LIGHT,
          }}
        >
          Existing tickets: <strong>{completed ? existingRows.length : existingTickets}</strong>
          {!completed && <> · Missing: <strong>{gap}</strong></>}
        </div>

        {/* Success banner */}
        {completed && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#ecfdf5',
            border: '1px solid #a7f3d0',
            borderRadius: '8px',
            marginBottom: '1.25rem',
            fontSize: '0.85rem',
            color: '#065f46',
            fontWeight: 500,
          }}>
            ✓ {createdCount} ticket{createdCount !== 1 ? 's' : ''} created successfully.
            {autoProvision ? ' Guest accounts provisioned.' : ''}
          </div>
        )}

        {/* Existing Tickets Table */}
        {(existingTickets > 0 || completed) && (
          <div style={{ marginBottom: '1.25rem' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '0.82rem', fontWeight: 600, color: TEXT_MUTED }}>
              {completed ? 'All Tickets in Order' : 'Existing Tickets in Order'}
            </h4>
            {loadingExisting ? (
              <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, padding: '8px 0' }}>Loading...</div>
            ) : existingRows.length === 0 ? (
              <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, padding: '8px 0' }}>No ticket records found.</div>
            ) : (
              <div style={{ border: '1px solid ' + BORDER_LIGHT, borderRadius: '8px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid ' + BORDER_LIGHT }}>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Ticket #</th>
                      <th style={thStyle}>Chapter</th>
                      <th style={thStyle}>Meal</th>
                      <th style={thStyle}>Seat / Table</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingRows.map((t, i) => (
                      <tr key={i} style={{ borderBottom: i < existingRows.length - 1 ? '1px solid ' + BORDER_LIGHT : undefined }}>
                        <td style={{ padding: '7px 10px', fontWeight: 500 }}>{t.fullName}</td>
                        <td style={{ padding: '7px 10px', fontFamily: 'monospace' }}>TKT-{String(t.ticketNumber).padStart(3, '0')}</td>
                        <td style={{ padding: '7px 10px', color: t.chapter ? TEXT_DARK : '#bbb' }}>{t.chapter || '—'}</td>
                        <td style={{ padding: '7px 10px', color: t.meal ? TEXT_DARK : '#bbb' }}>{t.meal || '—'}</td>
                        <td style={{ padding: '7px 10px', color: t.seatTable ? TEXT_DARK : '#bbb' }}>{t.seatTable || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* New Guests Entry — hidden after save */}
        {!completed && (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK }}>
                New Guests ({gap} ticket{gap !== 1 ? 's' : ''} to create)
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>First Name *</span>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Last Name *</span>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Chapter</span>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Meal</span>
              </div>

              {guests.map((g, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <input type="text" placeholder="First Name" value={g.firstName} onChange={(e) => updateGuest(i, 'firstName', e.target.value)} style={inputStyle} aria-label={`Guest ${i + 1} first name`} />
                  <input type="text" placeholder="Last Name" value={g.lastName} onChange={(e) => updateGuest(i, 'lastName', e.target.value)} style={inputStyle} aria-label={`Guest ${i + 1} last name`} />
                  <input type="text" placeholder="Chapter" value={g.chapter} onChange={(e) => updateGuest(i, 'chapter', e.target.value)} style={inputStyle} aria-label={`Guest ${i + 1} chapter`} />
                  <select value={g.meal} onChange={(e) => updateGuest(i, 'meal', e.target.value)} style={inputStyle} aria-label={`Guest ${i + 1} meal selection`}>
                    <option value="">— Meal —</option>
                    {mealOptions.map((m) => (<option key={m.id} value={m.name}>{m.name}</option>))}
                  </select>
                </div>
              ))}
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: TEXT_DARK, marginBottom: '1rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={autoProvision} onChange={(e) => setAutoProvision(e.target.checked)} />
              Auto-provision guest accounts
            </label>

            {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0 0 0.75rem' }}>{error}</p>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={onClose}
                disabled={submitting}
                style={{ padding: '8px 16px', fontSize: '0.82rem', fontWeight: 600, borderRadius: '6px', border: '1px solid ' + BORDER_LIGHT, backgroundColor: '#fff', color: TEXT_DARK, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{ padding: '8px 16px', fontSize: '0.82rem', fontWeight: 600, borderRadius: '6px', border: 'none', backgroundColor: PRIMARY, color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}
              >
                {submitting ? 'Creating...' : 'Create Tickets'}
              </button>
            </div>
          </>
        )}

        {/* Close button after successful save */}
        {completed && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => { onComplete(); }}
              style={{ padding: '10px 24px', fontSize: '0.85rem', fontWeight: 600, borderRadius: '6px', border: 'none', backgroundColor: PRIMARY, color: '#fff', cursor: 'pointer' }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
